import { Action, createReducer, on } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import * as SiteActions from './site.actions';
import {
  DetectionDensity,
  Site,
  VolumeDensity
} from '../../models/site';
import { SiteUserSettings } from '../../models/site-user-settings';


export interface State extends EntityState<Site> {
  selectedSiteId: string | undefined;
  searchSiteText: string | undefined
  siteDensityHighlight: DetectionDensity | VolumeDensity | undefined;
  userSettings: SiteUserSettings | null;
  userSettingsLoading: boolean;
  userSettingsUpdating: boolean;
}

export const adapter: EntityAdapter<Site> = createEntityAdapter<Site>();

export const initialState: State = adapter.getInitialState({
  selectedSiteId: undefined,
  searchSiteText: undefined,
  siteDensityHighlight: undefined,
  userSettings: null,
  userSettingsLoading: false,
  userSettingsUpdating: false,
});

const siteReducer = createReducer(
  initialState,
  on(SiteActions.selectSite, (state, { siteId }) => {
    return { ...state, selectedSiteId: siteId };
  }),
  on(SiteActions.setSearchSiteText, (state, { text }) => {
    return { ...state, searchSiteText: text };
  }),
  on(SiteActions.setSiteDensityHighlight, (state, { density }) => {
    return { ...state, siteDensityHighlight: density };
  }),
  on(SiteActions.loadSites, (state) => {
    return state;
  }),
  on(SiteActions.loadSitesSuccess, (state, { sites }) => {
    return adapter.setAll(sites, state);
  }),
  on(SiteActions.loadSitesFailure, (state, { error }) => {
    return state;
  }),
  on(SiteActions.retrieveSite, (state) => {
    return state;
  }),
  on(SiteActions.retrieveSiteSuccess, (state, { update }) => {
    return adapter.updateOne(update, state);
  }),
  on(SiteActions.retrieveSiteFailure, (state, { error }) => {
    return state;
  }),
  on(SiteActions.activateAdvancedFeatures, (state) => {
    return state;
  }),
  on(SiteActions.activateAdvancedFeaturesSuccess, (state, { update }) => {
    return adapter.updateOne(update, state);
  }),
  on(SiteActions.activateAdvancedFeaturesFailure, (state, { error }) => {
    return state;
  }),
  on(SiteActions.deactivateAdvancedFeatures, (state) => {
    return state;
  }),
  on(SiteActions.deactivateAdvancedFeaturesSuccess, (state, { update }) => {
    return adapter.updateOne(update, state);
  }),
  on(SiteActions.deactivateAdvancedFeaturesFailure, (state, { error }) => {
    return state;
  }),
  on(SiteActions.updateSite, (state, { update }) => {
    return adapter.updateOne(update, state);
  }),
  on(
    SiteActions.loadUserSettings,
    state => ({
      ...state,
      userSettings: null,
      userSettingsLoading: true,
    })
  ),
  on(
    SiteActions.loadUserSettingsSuccess,
    (state, {settings}) => ({
      ...state,
      userSettings: settings,
      userSettingsLoading: false,
    })
  ),
  on(
    SiteActions.loadUserSettingsFailure,
    state => ({
      ...state,
      userSettingsLoading: false,
    })
  ),
  on(
    SiteActions.updateUserSettings,
    state => ({
      ...state,
      userSettingsUpdating: true,
    })
  ),
  on(
    SiteActions.updateUserSettingsSuccess,
    (state, {settings}) => ({
      ...state,
      userSettings: settings,
      userSettingsUpdating: false,
    })
  ),
  on(
    SiteActions.updateUserSettingsFailure,
    state => ({
      ...state,
      userSettingsUpdating: false,
    })
  ),
);

export function reducer(state: State | undefined, action: Action): any {
  return siteReducer(state, action);
}

export const getSelectedSiteId = (state: State) => state.selectedSiteId;
export const getSearchSiteText = (state: State) => state.searchSiteText;
export const getSiteDensityHighlight = (state: State) => state.siteDensityHighlight;

